import React, { useState } from 'react';
import { Alert, Box, Chip, Grid, LinearProgress, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import { subDays } from 'date-fns';
import { useSnackbar } from 'notistack';
import PageTitle from '../../common/PageTitle';
import ProzoDataGrid from '../../common/ProzoDataGrid';
import { TableSkeleton } from '../../common/Skeletons';
import AwbLinkCell from '../../common/AwbLinkCell';
import ApiNeededNotice from '../../common/ApiNeededNotice';
import { apiConstants, getGeneric, getGenericBlob } from '../../common/constants';
import { pageContainerSx, dataGridWrapSx, badgeSx, btnRowSx, tabBarSx } from '../../styles/patterns';
import { BORDER, ERROR, NAVY, SUCCESS } from '../../styles/foundation/colors';
import { FONT, SIZE, WEIGHT } from '../../styles/foundation/typography';
import { formatDate } from '../../helpers/UtilityHelper';
import { getMerchant } from '../../helpers/ReduxHelpers';
import { awbTextSx, cellStackSx, primaryTextSx, secondaryTextSx, tertiaryTextSx } from '../orders/list2/cellStyles';
import FilterBar from '../appointments/shared/FilterBar';
import { saveCsvAsXlsx } from '../appointments/shared/exportHelpers';
import SectionHeading from './SectionHeading';
import usePtlApi from './usePtlApi';
import PodFileButton from './PodFileButton';
import { SplitBar, StatTile, tileMetaSx } from './PtlTiles';
import { POD_FALLBACK_BUCKETS, POD_MAX_DAYS, errorText, podBucketColor } from './constants';

const BASE = apiConstants.POD_ANALYTICS_MERCHANT;
const STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'RECEIVED', label: 'PoD received' },
  { value: 'PENDING', label: 'PoD pending' },
];
const SOURCE_LABEL = { API: 'API', MANUAL: 'Manual' };

const ymd = (d) => formatDate(d, 'yyyy-MM-dd');
const num = (n) => (n == null ? '-' : Number(n).toLocaleString('en-IN'));
// Server stamps are IST "yyyy-MM-dd HH:mm"; the T keeps every browser parsing them the same way.
const fmtStamp = (s) => (s ? formatDate(new Date(String(s).replace(' ', 'T')), 'dd MMM yyyy, h:mm a') : '-');
const dropBlank = (o) => Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined && v !== null && v !== ''));

const shipmentColumns = (bucketLabels) => [
  {
    field: 'awbNumber', headerName: 'AWB / Reference', minWidth: 190, flex: 1.1, sortable: false,
    renderCell: ({ row }) => (
      <Box sx={cellStackSx}>
        <AwbLinkCell awb={row.awbNumber} fallback='-' sx={{ ...awbTextSx, whiteSpace: 'nowrap' }} />
        {row.reference && <Typography sx={tertiaryTextSx}>Ref: {row.reference}</Typography>}
      </Box>
    ),
  },
  {
    field: 'podStatus', headerName: 'PoD', minWidth: 150, flex: 0.9, sortable: false,
    renderCell: ({ row }) => {
      if (row.podStatus !== 'PENDING') return <Box component='span' sx={badgeSx(SUCCESS)}>Received</Box>;
      const color = podBucketColor(row.agingBucket ?? 0, bucketLabels.length);
      const d = row.daysSinceDelivery ?? 0;
      return <Box component='span' sx={{ ...badgeSx(color), whiteSpace: 'nowrap' }}>Pending · {d} day{d === 1 ? '' : 's'}</Box>;
    },
  },
  {
    field: 'deliveredAt', headerName: 'Delivered On', minWidth: 160, flex: 0.9, sortable: false,
    renderCell: ({ row }) => <Typography sx={secondaryTextSx}>{fmtStamp(row.deliveredAt)}</Typography>,
  },
  {
    field: 'courierName', headerName: 'Courier', minWidth: 140, flex: 0.9, sortable: false,
    renderCell: ({ row }) => <Typography sx={primaryTextSx}>{row.courierName || '-'}</Typography>,
  },
  {
    field: 'podFile', headerName: 'PoD File', minWidth: 100, flex: 0.6, sortable: false,
    renderCell: ({ row }) => (row.podStatus === 'PENDING' ? <Typography sx={tertiaryTextSx}>-</Typography> : <PodFileButton awb={row.awbNumber} />),
  },
  {
    field: 'podUploadedAt', headerName: 'PoD Received On', minWidth: 170, flex: 1, sortable: false,
    renderCell: ({ row }) => (row.podStatus === 'PENDING' ? <Typography sx={tertiaryTextSx}>-</Typography> : (
      <Box sx={cellStackSx}>
        <Typography sx={secondaryTextSx}>{row.podUploadedAt ? fmtStamp(row.podUploadedAt) : 'Not tracked'}</Typography>
        {row.podSource && <Typography sx={tertiaryTextSx}>Via {SOURCE_LABEL[row.podSource] || row.podSource}</Typography>}
      </Box>
    )),
  },
  {
    field: 'destination', headerName: 'Destination', minWidth: 150, flex: 0.9, sortable: false,
    renderCell: ({ row }) => (
      <Box sx={cellStackSx}>
        <Typography sx={secondaryTextSx}>{row.destinationCity || '-'}</Typography>
        {row.destinationPincode && <Typography sx={tertiaryTextSx}>{row.destinationPincode}</Typography>}
      </Box>
    ),
  },
];

const courierColumns = [
  { field: 'name', headerName: 'Courier', minWidth: 180, flex: 1.4, sortable: false,
    renderCell: ({ row }) => <Typography sx={primaryTextSx}>{row.name || '-'}</Typography> },
  { field: 'delivered', headerName: 'Delivered', minWidth: 100, flex: 0.7, sortable: false,
    renderCell: ({ row }) => <Typography sx={secondaryTextSx}>{num(row.delivered)}</Typography> },
  { field: 'received', headerName: 'PoD Received', minWidth: 120, flex: 0.8, sortable: false,
    renderCell: ({ row }) => <Typography sx={secondaryTextSx}>{num(row.received)}{row.receivedPct != null ? ` (${row.receivedPct}%)` : ''}</Typography> },
  { field: 'pending', headerName: 'PoD Pending', minWidth: 110, flex: 0.7, sortable: false,
    renderCell: ({ row }) => <Typography sx={secondaryTextSx}>{num(row.pending)}</Typography> },
  { field: 'oldestPendingDays', headerName: 'Oldest Pending', minWidth: 120, flex: 0.8, sortable: false,
    renderCell: ({ row }) => <Typography sx={secondaryTextSx}>{row.oldestPendingDays != null ? `${row.oldestPendingDays} days` : '-'}</Typography> },
];

/**
 * PoD analytics for the logged-in merchant's delivered PTL shipments: delivered, PoDs received / pending and
 * PoD aging (server buckets, as the Admin Panel). Informational only: no reminders, settings or bulk actions.
 * Needs the merchant endpoints in docs/PTL_API_REQUIREMENTS.md §3.
 */
export default function PodAnalytics() {
  const { enqueueSnackbar } = useSnackbar();
  const [range] = useState(() => ({ fromDate: subDays(new Date(), 29), toDate: new Date() }));
  const [filters, setFilters] = useState(range);
  const [agingBucket, setAgingBucket] = useState(null);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);
  const [exporting, setExporting] = useState(false);
  // One table at a time: the shipment list or the courier split.
  const [view, setView] = useState('shipments');

  const from = filters.fromDate ? ymd(filters.fromDate) : '';
  const to = filters.toDate ? ymd(filters.toDate) : '';
  const rangeDays = from && to ? Math.round((new Date(to) - new Date(from)) / 86400000) + 1 : 0;
  const rangeError = !from || !to ? 'Pick a delivery date range.' : rangeDays > POD_MAX_DAYS ? `Pick at most ${POD_MAX_DAYS} days at a time.` : null;

  // merchantOid = the merchant picked in the panel. The server uses it only for Proship admins (their token keeps
  // the home merchant); merchant admins and users always get their token's merchant.
  const base = dropBlank({ from, to, courierId: filters.courier, merchantOid: getMerchant()?.id });
  // A picked aging bucket lists the pending PoDs of that bucket.
  const rowParams = dropBlank({
    ...base,
    podStatus: agingBucket != null ? 'PENDING' : filters.podStatus,
    agingBucket,
    awb: filters.awb ? filters.awb.trim() : '',
  });
  const summary = usePtlApi(() => getGeneric(`${BASE}/summary`, base), JSON.stringify(base), { skip: !!rangeError });
  const shipments = usePtlApi(() => getGeneric(`${BASE}/shipments`, { ...rowParams, page, size }),
    `${JSON.stringify(rowParams)}|${page}|${size}`, { skip: !!rangeError || summary.missing });

  const totals = summary.data?.totals || {};
  const bucketLabels = summary.data?.bucketLabels?.length ? summary.data.bucketLabels : POD_FALLBACK_BUCKETS;
  const pendingByBucket = totals.pendingByBucket || [];
  const loading = summary.loading;
  const val = (v) => (loading ? undefined : num(v ?? 0));

  const pickBucket = (i) => { setAgingBucket(agingBucket === i ? null : i); setPage(0); setView('shipments'); };
  const apply = (f) => { setFilters(f); setAgingBucket(null); setPage(0); };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await getGenericBlob(`${BASE}/shipments.csv`, rowParams);
      await saveCsvAsXlsx(res.data, `PTL_PoD_Shipments_${from}_to_${to}`);
      enqueueSnackbar('Export ready', { variant: 'success' });
    } catch (e) {
      enqueueSnackbar(errorText(e, 'Export failed. Please try again.'), { variant: 'error' });
    } finally {
      setExporting(false);
    }
  };

  const shipmentTotal = shipments.data?.total ?? 0;

  return (
    <Box sx={pageContainerSx}>
      <PageTitle subTitle='Proof of delivery for your delivered PTL shipments'>PoD Analytics</PageTitle>

      <FilterBar
        show={['courier', 'awb', 'dateRange']}
        initial={range}
        selects={[{ name: 'podStatus', label: 'PoD Status', options: STATUS_OPTIONS }]}
        onApply={apply}
        onReset={() => apply(range)}
      />

      {rangeError && <Alert severity='warning' sx={{ mb: 2 }}>{rangeError}</Alert>}

      {summary.missing ? (
        <ApiNeededNotice
          title='PoD analytics are not available for merchant logins yet'
          summary='PoD analytics run on an admin-only API today. This page shows your PoDs once the merchant-scoped API below is live.'
          points={[
            'Summary for your delivered PTL shipments: delivered, PoDs received, PoDs pending, on time, and pending PoDs by age.',
            'Shipment list with PoD status, age and source, paged, plus a CSV of the same list.',
          ]}
          endpoint={`GET /api/${BASE}/summary · GET /api/${BASE}/shipments · GET /api/${BASE}/shipments.csv`}
          footnote='The page fills in on its own once these APIs are live. Spec: docs/PTL_API_REQUIREMENTS.md.'
        />
      ) : (
        <>
          {summary.error && <Alert severity='error' sx={{ mb: 2 }}>{summary.error}</Alert>}

          <SectionHeading title='Proof of delivery'
            hint={from && to ? `Delivered ${formatDate(filters.fromDate, 'dd MMM')} – ${formatDate(filters.toDate, 'dd MMM yyyy')} · click an age to list those shipments` : ''} />
          <Grid container spacing={2} sx={{ mb: 2.5 }}>
            <Grid item xs={12} md={6}>
              <StatTile accent={SUCCESS} icon={<TaskAltIcon />} label='PoDs received'
                hint={`of ${num(totals.delivered ?? 0)} PTL shipments delivered`}
                value={val(totals.received)}
                bar={(
                  <LinearProgress variant='determinate' value={Math.min(100, totals.receivedPct || 0)} aria-label={`${totals.receivedPct || 0}% received`}
                    sx={{ height: 6, borderRadius: 3, bgcolor: BORDER, '& .MuiLinearProgress-bar': { bgcolor: SUCCESS, borderRadius: 3 } }} />
                )}
                footer={(
                  <Typography sx={tileMetaSx}>
                    <Box component='span' sx={{ color: SUCCESS, fontWeight: WEIGHT.semibold }}>{totals.receivedPct ?? 0}% received</Box>
                    {' · '}
                    {totals.onTimePct != null
                      ? `${totals.onTimePct}% on time${summary.data?.onTimeHours ? ` (within ${summary.data.onTimeHours} h of delivery)` : ''}`
                      : 'on time: not tracked yet'}
                  </Typography>
                )} />
            </Grid>
            <Grid item xs={12} md={6}>
              <StatTile accent={ERROR} icon={<PendingActionsIcon />} label='PoDs pending'
                hint={`Delivered, PoD not received${totals.oldestPendingDays != null ? ` · oldest ${totals.oldestPendingDays} days` : ''}`}
                value={val(totals.pending)}
                bar={<SplitBar label={bucketLabels.map((l, i) => `${l} ${pendingByBucket[i] || 0}`).join(', ')}
                  segments={bucketLabels.map((l, i) => ({ key: l, value: pendingByBucket[i] || 0, color: podBucketColor(i, bucketLabels.length) }))} />}
                footer={(
                  <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                    {bucketLabels.map((l, i) => {
                      const n = pendingByBucket[i] || 0;
                      const color = podBucketColor(i, bucketLabels.length);
                      const selected = agingBucket === i;
                      return (
                        <Tooltip key={l} arrow title={`${totals.pending ? Math.round((100 * n) / totals.pending) : 0}% of pending · click to list them`}>
                          <Chip size='small' clickable onClick={() => pickBucket(i)} variant={selected ? 'filled' : 'outlined'}
                            label={`${l} · ${num(n)}`}
                            icon={<Box component='span' sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: color, ml: '8px !important' }} />}
                            sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.semibold, textTransform: 'none',
                              '& .MuiChip-label': { textTransform: 'none' },
                              ...(selected ? { bgcolor: NAVY, color: '#fff', '&:hover': { bgcolor: NAVY } } : { borderColor: BORDER, color: NAVY }) }} />
                        </Tooltip>
                      );
                    })}
                  </Box>
                )} />
            </Grid>
          </Grid>

          <Tabs value={view} onChange={(_e, v) => setView(v)} sx={{ ...tabBarSx, mb: 2 }}>
            <Tab value='shipments' label={`Shipments${shipments.loading ? '' : ` (${num(shipmentTotal)})`}`} />
            <Tab value='courier' label={`By courier${summary.data?.byCourier ? ` (${summary.data.byCourier.length})` : ''}`} />
          </Tabs>

          {view === 'shipments' ? (
            <>
              <Box sx={{ ...btnRowSx, mb: 1.5 }}>
                <Typography sx={tertiaryTextSx}>
                  {agingBucket != null ? `PoD pending · ${bucketLabels[agingBucket]}` : 'Every delivered shipment with its PoD status'}
                  {shipments.loading ? '' : ` · ${num(shipmentTotal)} shipments`}
                </Typography>
                {agingBucket != null && (
                  <Chip size='small' label={`Aging: ${bucketLabels[agingBucket]}`} onDelete={() => pickBucket(agingBucket)} />
                )}
                <Box sx={{ ml: 'auto' }}>
                  <LoadingButton size='small' variant='outlined' startIcon={<FileDownloadOutlinedIcon />} loading={exporting}
                    disabled={!shipmentTotal} onClick={exportCsv}>Export</LoadingButton>
                </Box>
              </Box>
              {shipments.error && !shipments.missing ? (
                <Alert severity='error'>{shipments.error}</Alert>
              ) : shipments.loading && !shipments.data?.rows?.length ? (
                <TableSkeleton rows={6} columns={shipmentColumns(bucketLabels).length} />
              ) : (
                <Box sx={dataGridWrapSx}>
                  <ProzoDataGrid
                    columns={shipmentColumns(bucketLabels)}
                    rows={shipments.data?.rows || []}
                    rowCount={shipmentTotal}
                    paginationMode='server'
                    pagination
                    page={page}
                    setPage={setPage}
                    sizePerPage={size}
                    setSizePerPage={(s) => { setSize(s); setPage(0); }}
                    loading={shipments.loading}
                    getRowId={(r) => r.awbNumber}
                    boxShadow={0}
                  />
                </Box>
              )}
            </>
          ) : loading && !summary.data ? (
            <TableSkeleton rows={4} columns={courierColumns.length} />
          ) : (
            <Box sx={dataGridWrapSx}>
              <ProzoDataGrid
                columns={courierColumns}
                rows={summary.data?.byCourier || []}
                rowCount={(summary.data?.byCourier || []).length}
                paginationMode='client'
                hideFooterPagination
                loading={loading}
                getRowId={(r) => r.key || r.name}
                boxShadow={0}
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
